import { Component, EventEmitter, Inject, Input, Output } from "@angular/core";

import { Observable, Subject, combineLatest } from "rxjs";
import { map } from "rxjs/operators";

import { MatTableDataSource } from "@angular/material/table";

import { CommandRelay } from '../../../../shared/commands/command-relay.class';

import { CommunicationMutuelleInfoEntity } from "../../entities/communication-mutuelle-info.entity";
import { CommunicationMutuelleInfoService } from "../../services/communication-mutuelle-info.service";
import { PlanPreventionService } from "../../services/plan-prevention.service";
import { PlanPreventionEntity } from "../../entities/plan-prevention.entity";
import { SubjectRelay } from "../../../../shared/commands/subject-relay.class";

enum TypeOperation {
	TOUTES,
	TRAVAUX_ELECTRIQUES,
	TRAVAUX_EQUIPEMENTS_HYDRAULIQUES
}

@Component({
	selector: 'communication-mutuelle-info',
	templateUrl: 'communication-mutuelle-info.html',
	styleUrls: ['communication-mutuelle-info.css']
})
export class CommunicationMutuelleInfoComponent {
	/* Communication mutuelle d'informations */
	toutesOperationsTable: MatTableDataSource<CommunicationMutuelleInfoEntity> = new MatTableDataSource<CommunicationMutuelleInfoEntity>();
	travauxElectriquesTable: MatTableDataSource<CommunicationMutuelleInfoEntity> = new MatTableDataSource<CommunicationMutuelleInfoEntity>();
	travauxEquipementsHydrauliquesTable: MatTableDataSource<CommunicationMutuelleInfoEntity> = new MatTableDataSource<CommunicationMutuelleInfoEntity>();
	displayedColumnsCommunicationMutuelle: string[] = ['selected', 'information', 'emplacement', 'fournisseur'];
	selectedComIds: number[] = [];
	
	private energieSubject = new Subject<string | null>();
	private isNew: boolean = false;

	constructor(
		@Inject(CommunicationMutuelleInfoService) private communicationMutuelleInfoService: CommunicationMutuelleInfoService,
		@Inject(PlanPreventionService) private planPreventionService: PlanPreventionService,
	) {
		// Chargement des types de communication
		let communicationMutellesInfo$ = this.communicationMutuelleInfoService.getAllCommunicationMutuelleInfo();
		
		// Definir les communications autorisees selon les energies et communications connunes
		let communicationsPossiblesByEnergie$ = communicationMutellesInfo$.pipe(
			map(
				(communications) => {
					let map = new Map<string, CommunicationMutuelleInfoEntity[]>();
					
					let eolienneInfos = communications.filter(comm => comm.category === 0 || comm.category === 2);
					map.set("Eolienne", eolienneInfos);
					
					let solaireInfos = communications.filter(comm => comm.category === 1 || comm.category === 2);
					map.set("Solaire", solaireInfos);
					
					return map;
				}
			)
		);
		
		// Definir les communications autorisees en fonction de l'energie actuellement selectionnne via le parc dans le pdp
		let energieCommunications$ = combineLatest([communicationsPossiblesByEnergie$, this.energieSubject]).pipe(
			map(
				(data) => {
					let communicationsPossiblesByEnergie = data[0];
					let energie = data[1];
					if (!energie) {
						return [];
					}
					
					let energieCommunications = communicationsPossiblesByEnergie.get(energie);
					if (!energieCommunications) {
						throw new Error("Type d'energie non reconnu: " + energie);
					}
					
					return energieCommunications;
				}
			)
		);

		// Quand le composant demarre son initialisation
		let reinitForNew$ = new Subject<void>();
		let reinitWithExisting$ = new Subject<CommunicationMutuelleInfoEntity[]>();
		this.communicationMutuelleCommandsSubject.subscribe(
			(pdp) => {
				if (pdp.id) {
					this.isNew = false;
					// Cas d'un pdp existant : charger les communications de ce pdp
					this.planPreventionService.getCommunicationMutuelles(pdp.id).subscribe(
						(data) => reinitWithExisting$.next(data.communicationMutuelleInfo)
					);
				} else {
					this.isNew = true;
					reinitForNew$.next();
				}
			}
		)
		
		// Dans le cas d'une reinitialisation pour un nouveau PDP, une fois les communications liees a l'energie disponibles 
		combineLatest([energieCommunications$, reinitForNew$]).subscribe(
			(data) => {
				let energyCommunications = data[0];
				
				this.toutesOperationsTable.data = energyCommunications.filter(value => value.type === TypeOperation.TOUTES);
				this.travauxElectriquesTable.data = energyCommunications.filter(value => value.type === TypeOperation.TRAVAUX_ELECTRIQUES);
				this.travauxEquipementsHydrauliquesTable.data = energyCommunications.filter(value => value.type === TypeOperation.TRAVAUX_EQUIPEMENTS_HYDRAULIQUES);
			}
		);
		
		// Dans le cas d'une reinitialisation pour PDP existant, une fois les communications liees a l'energie disponibles 
		combineLatest([energieCommunications$, reinitWithExisting$]).subscribe(
			(data) => {
				let energyCommunications = data[0];
				let pdpCommunications = this.isNew ? [] : data[1];
				
				this.selectedComIds = pdpCommunications != null ? pdpCommunications.map($ => $.id) : [];
				this.toutesOperationsTable.data = energyCommunications.filter(value => value.type === TypeOperation.TOUTES).map($ => {
					$.selected = this.selectedComIds.indexOf($.id) > -1;
					this.onModelChange.emit($);
					return $;
				});
				this.travauxElectriquesTable.data = energyCommunications.filter(value => value.type === TypeOperation.TRAVAUX_ELECTRIQUES).map($ => {
					$.selected = this.selectedComIds.indexOf($.id) > -1;
					this.onModelChange.emit($);
					return $;
				});
				this.travauxEquipementsHydrauliquesTable.data = energyCommunications.filter(value => value.type === TypeOperation.TRAVAUX_EQUIPEMENTS_HYDRAULIQUES).map($ => {
					$.selected = this.selectedComIds.indexOf($.id) > -1;
					this.onModelChange.emit($);
					return $;
				});
			}
		);
	}

	private communicationMutuelleCommandsSubject = new SubjectRelay<PlanPreventionEntity>();
	@Input() set communicationMutuelleCommands(obs: Observable<PlanPreventionEntity>) {
		this.communicationMutuelleCommandsSubject.registerSource(obs);
	}
	
	private energieChangeCommandRelay = new CommandRelay<string | null>((energie) => this.energieSubject.next(energie));
	@Input() set energieChangeCommand(obs: Observable<string | null>) {
		this.energieChangeCommandRelay.register(obs);
	}

	@Output() onModelChange = new EventEmitter<CommunicationMutuelleInfoEntity | undefined>();

	onChangeToutes = (communicationMutuelle: CommunicationMutuelleInfoEntity) => {
		this.onModelChange.emit(communicationMutuelle);
	}

	onChangeTravauxElectrique = (communicationMutuelle: CommunicationMutuelleInfoEntity) => {
		this.onModelChange.emit(communicationMutuelle);
	}

	onChangeTravauxEquipementsHydrauliques = (communicationMutuelle: CommunicationMutuelleInfoEntity) => {
		this.onModelChange.emit(communicationMutuelle);
	}
}